import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { NgbModal, NgbModalOptions} from '@ng-bootstrap/ng-bootstrap';
import { SCROLL_TO_OFFERS_QUERYPARAM } from 'src/app/constants/app.constants';
import { Alert } from 'src/app/interfaces/alert';
import { Pet, PetSpeciesFilterableType } from 'src/app/interfaces/pet';
import { PetplanTrialApplication } from 'src/app/interfaces/petplan';
import { User, WelcomeGiftStepState } from 'src/app/interfaces/user';
import { LogService } from 'src/app/services/common/log.service';
import { PerkService } from 'src/app/services/perk/perk.service';
import { RegistrationProgressData } from 'src/app/services/registration-progress/registration-progress.service';
import { UserService } from 'src/app/services/user/user.service';
import { PetplanDeclineInsuranceModalComponent } from 'src/app/modals/petplan-decline-insurance-modal/petplan-decline-insurance-modal.component';

@Component({
  selector: 'app-petplan-welcome-gift',
  templateUrl: './petplan-welcome-gift.component.html',
  styleUrls: ['./petplan-welcome-gift.component.scss'],
})
export class PetplanWelcomePackComponent {
  public alert: Alert = null;
  public loading = false;
  public user: User;
  public eligiblePets: Pet[];
  public petNames: string;
  public petSpecie: string;
  public bannerImagePath: string;
  public eligiblePet: Pet;

  constructor(
    private router: Router,
    private logService: LogService,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private perkService: PerkService,
    private _modalService: NgbModal,
  ) {
    this.user = this.activatedRoute.snapshot.data.user as User;
    this.eligiblePets = this.activatedRoute.snapshot.data.eligiblePets as Pet[];
    this.eligiblePet = this.getLastItem();

    this.bannerImagePath = this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Dog
    ? "/assets/images/partner/petplan/banner/banner-dog.png"
    : this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Cat
    ? "/assets/images/partner/petplan/banner/banner-cat.png"
    : "/assets/images/partner/petplan/banner/banner-rabbit.png"
  }

  ngOnInit(): void {
    this.petNames = this.createTitle();
    if (this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Dog) {
      this.petSpecie = "Dog";
    } else if (this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Cat) {
      this.petSpecie = "Cat";
    } else {
      this.petSpecie = "Rabbit";
    }
  }

  private createTitle(): string {
    if (!!this.eligiblePets && this.eligiblePets.length > 0) {
      return `${this.createPetsListString(this.eligiblePets)}.`;
    }

    // If there are no eligible pets, redirect to dashboard.
    this.alert = {
      type: 'info',
      message:
        'You do not have any eligible pets for petplan. You\'ll get redirected in a few seconds!',
    };
    setTimeout(() => {
      this.router.navigate(['/dashboard'], { queryParams: { followUs: 'open' } });
    }, 1000);
    return;
  }

  private createPetsListString(pets): string {
    if (!pets.length) {
      return '';
    } else if (pets.length === 1) {
      return pets[0].name;
    } else if (pets.length === 2) {
      return `${pets[0].name} & ${pets[1].name}`;
    } else if (pets.length === 3) {
      return `${pets[0].name}, ${pets[1].name} & ${pets[2].name}`;
    } else {
      return `${pets[0].name}, ${pets[1].name} & your other pets`;
    }
  }

  public noToPetInsurance() {
    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
    };

    const modalRef = this._modalService.open(PetplanDeclineInsuranceModalComponent, modalOptions);

    modalRef.componentInstance.eligiblePet = this.eligiblePet
    modalRef.result.then(
    (result) => {
      this.completeWelcomeGift(result)
      },
    );
  }

  public completeWelcomeGift(claimGift: boolean) {
    if (!this.loading) {
      this.updateUserDetails()
        .subscribe(
          (updatedUser) => {
            this.user = updatedUser;
            this.alert = {
              type: 'success',
              message:
                'Your account information was updated. You\'ll get redirected in a few seconds!',
            };
            setTimeout(() => {
              claimGift
                ? this.sendPetplanApplication()
                : this.router.navigate(['/dashboard'], {
                    queryParams: { scrollTo: SCROLL_TO_OFFERS_QUERYPARAM, followUs: 'open' },
                  });
            }, 1000);
            return;
          },
          (err) => {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
            this.logService.error(
              'An error has occurred during welcome gift state save. Please try again later. ',
              err
            );
          }
        )
        .add(() => {
          this.loading = false;
        });
    }
  }

  public closeAlert() {
    this.alert = null;
  }

  private updateUserDetails(): Observable<User> {
    this.closeAlert();
    this.loading = true;
    const userChanges: Partial<User> = {
      welcomeGiftStepCompleted:
        this.user.welcomeGiftStepCompleted === WelcomeGiftStepState.NotUpdated
          ? WelcomeGiftStepState.Updated
          : this.user.welcomeGiftStepCompleted,
    };

    return this.userService.patchMe(userChanges);
  }

  private getLastItem(): Pet {
    return this.eligiblePets.length ? this.eligiblePets[this.eligiblePets.length - 1] : null;
  }

  private sendPetplanApplication() {
    this.closeAlert();
    this.loading = true;
    if (this.user.pets.length < 1){
      this.alert = {
        type: 'danger',
        message: 'You do not have any pets registered in our system. Please come back after you have registered your pet.',
      };
      return;
    }

    const request: PetplanTrialApplication = {
      uw1: false,
      uw2: false,
      uw3: false,
      receiveCompany: false
    };

    this.perkService.submitPetplanTrialApplication(request).subscribe(
      (res) => {},
      (err) => {
        console.error(err);
        this.alert = {
          type: 'danger',
          message:
            'There was an issue submitting your Petplan trial application. Please try again later.',
        };
      },
      () => {
        this.perkService.sendPetplanCoverNoteEmail().subscribe(
          (res) => {},
          (err) => {
            console.error(err);
          }
        );

        this.loading = false;
        this.router.navigate(['/dashboard'],  { queryParams: { followUs: 'open' } });
      }
    );
  }

  public getProgressData(){
    const result: RegistrationProgressData = {
      isFullNameRegistrationCompleted: true,
      isPetChipNumberRegistrationCompleted: true,
      isMembershipSelectionCompleted: true,
      isWelcomePackSelectCompleted: false,
    };
    return result;
  }
}
